import {
    IAprioriConfidence, IAprioriLift,
    IAprioriParameters,
    IAprioriResult,
    IAprioriSupport, IAssociationRule,
} from '../apriori-interfaces';

const getLetters = (rows: string[]): string[] => {
    const set : Set<string> = new Set();

    for(const row of rows) {
        const letters = row.split('');
        for(const letter of letters) {
            set.add(letter);
        }
    }

    const letters = [...set];
    letters.sort((a, b) => a.localeCompare(b));
    return letters;
};

/**
 * support = number_of_presence / number_of_rows
 */
const calculateCombinationSupport = (combination: string, rows: string[]) : IAprioriSupport => {

    if(rows.length === 0) return {
        instances: 0,
        total: 0,
        result: 0,
    };


    let lettersPresenceNumber = 0;

    for(const row of rows) {
        const letters = [...new Set(combination.split(''))];
        let notIncludesAtLeastOneLetter = false;

        for(const letter of letters) {
            if(!row.includes(letter)) {
                notIncludesAtLeastOneLetter = true;
            }
        }

        if(!notIncludesAtLeastOneLetter) {
            lettersPresenceNumber++;
        }
    }

    return {
        instances: lettersPresenceNumber,
        total: rows.length,
        result: lettersPresenceNumber / rows.length,
    }
};

const getLetterCombinations = (letters: string[], length: number): string[][] => {
    const combinations: string[][] = [];

    const combine = (start: number, path: string[]) => {
        if(path.length === length) {
            combinations.push([...path]);
        }

        for (let i = start; i < letters.length; i++) {
            path.push(letters[i]);
            combine(i + 1, path);
            path.pop();
        }
    };

    combine(0, []);

    return combinations;
};

const getRuleConfidence = (rule: [string, string], rows: string[]) : IAprioriConfidence => {
    const left = rule[0];
    const right = rule[1];

    const topSupport = calculateCombinationSupport(left + right, rows);
    const bottomSupport = calculateCombinationSupport(left, rows);

    return {
        topSupport,
        bottomSupport,
        result: bottomSupport.result === 0 ? 0 : (topSupport.result / bottomSupport.result),
    }
};

const getRuleLift = (rule: [string, string], rows: string[]) : IAprioriLift => {
    const left = rule[0];
    const right = rule[1];

    const top = calculateCombinationSupport(left + right, rows);
    const bottom1 = calculateCombinationSupport(left, rows);
    const bottom2 = calculateCombinationSupport(right, rows);

    const temp = bottom1.result * bottom2.result;

    return {
        top,
        bottom1,
        bottom2,
        result: temp === 0 ? 0 : (top.result / temp),
    }
};

const getCombinationsSupport = (combinations: string[][], rows: string[]): Map<string, IAprioriSupport> => {
    const map: Map<string, IAprioriSupport> = new Map(); // letter --> support

    for(const combination of combinations) {
        combination.sort((a, b) => a.localeCompare(b));
        const joined = combination.join('');
        const support = calculateCombinationSupport(joined, rows);
        map.set(joined, support);
    }

    return map;
};

const filterSupportMap = (map: Map<string, IAprioriSupport>, minSupport: number): Map<string, IAprioriSupport> => {
    const filteredMap: Map<string, IAprioriSupport> = new Map(); // letter -> support

    for(const [letter, support] of map) {
        if(support.result >= minSupport) {
            filteredMap.set(letter, support);
        }
    }

    return filteredMap;
};

const getLettersFromFilteredSupportMap = (filteredSupportMap: Map<string, IAprioriSupport>): string[] => {
    const set: Set<string> = new Set();
    const keys = filteredSupportMap.keys();

    for(const key of keys) {
        const split = key.split('');
        for(const letter of split) {
            set.add(letter);
        }
    }

    const res = [...set];
    res.sort((a, b) => a.localeCompare(b));

    return res;
};

export const createFrequencyGroupsPermutations = (frequencyGroups: string[]) : [string, string][] => {
    const permutations: [string, string][] = [];

    for (let i = 0; i < frequencyGroups.length; i++) {
        for (let j = 0; j < frequencyGroups.length; j++) {
            if (i !== j) {
                permutations.push([frequencyGroups[i], frequencyGroups[j]]);
            }
        }
    }

    return permutations;
};

export const removeSelfRepetitionRules = (rules: [string, string][]) : [string, string][] => {
    const filteredRules: [string, string][] = [];

    const used: Set<string> = new Set();

    for(const rule of rules) {
        const left = new Set(rule[0].split(''));
        const right = new Set(rule[1].split(''));

        for(const letter of left) {
            if(right.has(letter)) {
                left.delete(letter);
                right.delete(letter);
            }
        }

        for(const letter of right) {
            if(left.has(letter)) {
                right.delete(letter);
                left.delete(letter);
            }
        }

        if(left.size <= 0 || right.size <= 0) continue;

        const leftArr = [...left];
        leftArr.sort((a, b) => a.localeCompare(b));

        const rightArr = [...right];
        rightArr.sort((a, b) => a.localeCompare(b));

        const leftJoined = leftArr.join('');
        const rightJoined = rightArr.join('');

        const key = `${ leftJoined }-${ rightJoined }`;
        if(used.has(key)) continue;

        used.add(key);

        filteredRules.push([
            leftJoined,
            rightJoined,
        ]);
    }

    return filteredRules;
};

export const getAssociationRules = (
    filteredPermutations: [string, string][],
    rows: string[],
    minConfidence: number
) : IAssociationRule[] => {
    const rules: IAssociationRule[] = [];

    for(const permutation of filteredPermutations) {
        const confidence = getRuleConfidence(permutation, rows);
        const lift = getRuleLift(permutation, rows);

        rules.push({
            rule: permutation,
            confidence,
            lift,
            isStrong: confidence.result >= minConfidence,
        })
    }

    return rules;
};

export const calculateApriori = (params: IAprioriParameters): IAprioriResult => {

    const results: IAprioriResult = {
        maps: [],
        frequencyGroups: [],
        associationRules: [],
    }

    let letters = getLetters(params.rows);

    let length = 1;
    let combinations = getLetterCombinations(letters, length);

    while(letters.length > 0) {
        combinations = getLetterCombinations(letters, length);
        const supportMap = getCombinationsSupport(combinations, params.rows);

        results.maps.push(new Map(supportMap));

        const filteredSupportMap = filterSupportMap(supportMap, params.minSupport);

        const keys = [...filteredSupportMap.keys()];
        results.frequencyGroups.push(...keys);

        results.maps.push(new Map(filteredSupportMap));

        letters = getLettersFromFilteredSupportMap(filteredSupportMap);

        // console.log('combinations', combinations, 'supportMap', supportMap, 'filteredSupportMap', filteredSupportMap, 'letters', letters);

        length++;
    }

    const permutations = createFrequencyGroupsPermutations(results.frequencyGroups);
    const filteredPermutations = removeSelfRepetitionRules(permutations);
    results.associationRules = getAssociationRules(filteredPermutations, params.rows, params.minConfidence);

    return results;
};